import React from 'react';

/**
 * ResponsiveCard - A card component that adapts to different screen sizes
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.variant - Card style variant ('default', 'elevated', 'outline')
 * @param {string} props.padding - Padding size ('sm', 'md', 'lg')
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.hover - Whether to show hover effects
 */
const ResponsiveCard = ({
  children,
  variant = 'default',
  padding = 'md',
  className = '',
  hover = false,
  ...props
}) => {
  const variantClasses = {
    'default': 'bg-white shadow-card',
    'elevated': 'bg-white shadow-card-hover',
    'outline': 'bg-white border border-gray-200'
  };

  const paddingClasses = {
    'sm': 'p-3 sm:p-4',
    'md': 'p-4 sm:p-6',
    'lg': 'p-6 sm:p-8'
  };

  const baseClasses = [
    'rounded-2xl',
    'transition-all',
    'duration-200',
    'ease-in-out',
    variantClasses[variant],
    paddingClasses[padding],
    hover && 'hover:shadow-card-hover hover:scale-[1.02]',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={baseClasses} {...props}>
      {children}
    </div>
  );
};

/**
 * ResponsiveCardHeader - Card header component
 */
export const ResponsiveCardHeader = ({ children, className = '', ...props }) => (
  <div className={`mb-4 ${className}`} {...props}>
    {children}
  </div>
);

/**
 * ResponsiveCardTitle - Card title component
 */
export const ResponsiveCardTitle = ({ children, className = '', ...props }) => (
  <h3 className={`text-lg sm:text-xl font-semibold text-gray-900 ${className}`} {...props}>
    {children}
  </h3>
);

/**
 * ResponsiveCardContent - Card content component
 */
export const ResponsiveCardContent = ({ children, className = '', ...props }) => (
  <div className={`text-sm sm:text-base text-gray-600 ${className}`} {...props}>
    {children}
  </div>
);

/**
 * ResponsiveCardFooter - Card footer component
 */
export const ResponsiveCardFooter = ({ children, className = '', ...props }) => (
  <div className={`mt-4 pt-4 border-t border-gray-100 ${className}`} {...props}>
    {children}
  </div>
);

export default ResponsiveCard;